import { HttpErrorResponse } from '@angular/common/http';
import { IContentPageResponse } from '@modeso/dgoods-lib-admin-fe/models/content.pages';
import { IContentBoxResponse, INewstickerResponse, ISpotLight } from '@modeso/types__dgoods-products';
import { Action, createReducer, createSelector, on } from '@ngrx/store';
import { InternationalTopUpOfferItem } from '../models/international-top-up-offer-item.interface';
import { InternationalTopUpOffersOrOperators } from '../models/international-top-up-offers-or-operators.interface';
import { InternationalTopUpPhoneContext } from '../models/international-top-up-phone-context.interface';
import { InternationalTopUpSelectedCarrier } from '../models/international-top-up-selected-carrier.interface';
import { ShopCategory } from '../models/shop-category.interface';
import { ShopProductLine } from '../models/shopProductLine.interface';
import * as productActions from './../actions/products.actions';
export const productsFeatureKey = 'modesoProductsMicroservice';

// State Declarations - START

export interface FeatureState {
    products: ShopProductLine[];
    categories: ShopCategory[];
    activeCategoryFilter: string;
    newsTicker: INewstickerResponse | undefined;
    menuItems: Array<any>;
    contentPagesList: IContentPageResponse[];
    spotlights: ISpotLight[];
    contentBox: IContentBoxResponse;
    internationalTopUpLookupResult: InternationalTopUpOffersOrOperators | undefined;
    internationalTopUpPhoneContext: InternationalTopUpPhoneContext | undefined;
    internationalTopUpSelectedOffer: InternationalTopUpOfferItem | undefined;
    internationalTopUpSelectedCarrier: InternationalTopUpSelectedCarrier | undefined;
    isInternationalTopUpLookupLoading: boolean;
    isInternationalTopUpOfferSelectionSucceeded: boolean | undefined;
    userHasSpotlights: boolean;
    isLandingBuyLoading: boolean;
    error: HttpErrorResponse | undefined;
}

export interface AppState {
    modesoProductsMicroservice: FeatureState;
}

// State Declarations - END

// Selectors Declarations - START

export const selectFeature = (state: AppState) => state.modesoProductsMicroservice;
export const selectFeatureProducts = createSelector(selectFeature, (state: FeatureState) => state.products);

export const selectActiveCategoryFilter = createSelector(
    selectFeature,
    (state: FeatureState) => state.activeCategoryFilter,
);

export const selectLandingBuyLoadingState = createSelector(
    selectFeature,
    (state: FeatureState) => state.isLandingBuyLoading,
);

export const selectFeatureCategories = createSelector(selectFeature, (state: FeatureState) => state.categories);

export const selectFeatureContentPagesList = createSelector(
    selectFeature,
    (state: FeatureState) => state.contentPagesList,
);
export const selectFeatureContentBox = createSelector(selectFeature, (state: FeatureState) => state.contentBox);

export const selectProductById = (productId: string) =>
    createSelector(selectFeature, (state: FeatureState) => state.products.find((p) => p.productLineId === productId));

export const selectFeatureNewsTicker = createSelector(selectFeature, (state: FeatureState) => state.newsTicker);

export const selectMenuItems = createSelector(selectFeature, (state: FeatureState) => state.menuItems);

export const selectSpotlights = createSelector(selectFeature, (state: FeatureState) => state.spotlights);

export const selectUserHasSpotlights = createSelector(selectFeature, (state: FeatureState) => state.userHasSpotlights);

export const selectInternationalTopUpLookupResult = createSelector(
    selectFeature,
    (state: FeatureState) => state.internationalTopUpLookupResult,
);

export const selectInternationalTopUpLookupLoadingState = createSelector(
    selectFeature,
    (state: FeatureState) => state.isInternationalTopUpLookupLoading,
);

export const selectInternationalTopUpPhoneContext = createSelector(
    selectFeature,
    (state: FeatureState) => state.internationalTopUpPhoneContext,
);

export const selectInternationalTopUpSelectedOffer = createSelector(
    selectFeature,
    (state: FeatureState) => state.internationalTopUpSelectedOffer,
);

export const selectInternationalTopUpSelectedCarrier = createSelector(
    selectFeature,
    (state: FeatureState) => state.internationalTopUpSelectedCarrier,
);

export const selectInternationalTopUpOfferSelectionSucceeded = createSelector(
    selectFeature,
    (state: FeatureState) => state.isInternationalTopUpOfferSelectionSucceeded,
);

export const selectFeatureError = createSelector(selectFeature, (state: FeatureState) => state.error);

export const initialState: FeatureState = {
    products: [],
    categories: [],
    activeCategoryFilter: 'ALL',
    menuItems: [
        { title: 'tdv_menu_credits', icons: 'tdv_menu_credits.svg' },
        { title: 'tdv_menu_email', icons: 'tdv_menu_email.svg' },
        { title: 'tdv_menu_faq', icons: 'tdv_menu_faq.svg' },
        { title: 'tdv_menu_tAndC', icons: 'tdv_menu_tAndC.svg' },
    ],
    newsTicker: undefined,
    contentPagesList: [],
    spotlights: [],
    userHasSpotlights: undefined,
    contentBox: undefined,
    internationalTopUpLookupResult: undefined,
    internationalTopUpPhoneContext: undefined,
    internationalTopUpSelectedOffer: undefined,
    internationalTopUpSelectedCarrier: undefined,
    isInternationalTopUpLookupLoading: false,
    isInternationalTopUpOfferSelectionSucceeded: undefined,
    isLandingBuyLoading: false,
    error: undefined,
};

const productsReducer = createReducer(
    initialState,
    on(productActions.getAllProducts, (state) => ({ ...state })),
    on(productActions.onAllProductsLoadedSuccessfully, (state, action) => {
        const { productLines, categories } = action.payload;

        const productLineNameToProductLine = new Map<string, ShopProductLine>(
            productLines.map((productLine: ShopProductLine) => [productLine.productLineName, productLine]),
        );

        const mappedCategoriesToProducts = categories.map((category) => {
            const mappedProducts = category.productLineNames.map((name) => {
                const pl = productLineNameToProductLine.get(name);
                if (!pl) {
                    throw new Error(`Unknown product line: ${name}`);
                }

                return { ...pl };
            });
            return { ...category, productLineNames: mappedProducts };
        });

        return { ...state, products: productLines, categories: mappedCategoriesToProducts, error: undefined };
    }),
    on(productActions.onAllProductsLoadingFailed, (state) => ({ ...state })),

    on(productActions.setActiveCategoryFilter, (state, action) => ({ ...state, activeCategoryFilter: action.payload })),

    on(productActions.setLandingPageLoadingState, (state, action) => ({
        ...state,
        isLandingBuyLoading: action.payload,
    })),

    on(productActions.getNewsTicker, (state) => ({ ...state, newsTicker: undefined })),
    on(productActions.onGetNewsTickerSucceeded, (state, action) => ({ ...state, newsTicker: action.payload })),
    on(productActions.onGetNewsTickerFailed, (state, action) => {
        return { ...state, newsTicker: undefined, error: action.payload };
    }),
    on(productActions.getAllContentPages, (state) => ({ ...state })),
    on(productActions.getAllContentPagesSuccess, (state, action) => ({ ...state, contentPagesList: action.payload })),
    on(productActions.getAllContentPagesFailure, (state, action) => ({
        ...state,
        contentPagesList: [],
        error: action.payload,
    })),

    on(productActions.getSpotlights, (state) => ({ ...state })),
    on(productActions.getSpotlightsSuccessfully, (state, action) => {
        return { ...state, spotlights: action.payload.spotlights, userHasSpotlights: action.payload.userHasSpotlights };
    }),
    on(productActions.getSpotlightsFailed, (state, action) => ({ ...state, spotlights: [], error: action.payload })),

    on(productActions.markSpotlightAsRead, (state) => ({ ...state })),
    on(productActions.markSpotlightAsReadSuccessfully, (state, action) => {
        const updatedSpotlights = [...state.spotlights];
        const updatedIndex = updatedSpotlights.findIndex((spotlight) => spotlight.id === action.payload.spotLightId);
        updatedSpotlights.splice(updatedIndex, 1);
        return { ...state, spotlights: [...updatedSpotlights], error: undefined };
    }),
    on(productActions.markSpotlightAsReadSuccessfully, (state, action) => ({ ...state, error: action.payload })),

    on(productActions.getPublishedContentBox, (state) => ({ ...state })),
    on(productActions.getPublishedContentBoxSuccess, (state, action) => ({ ...state, contentBox: action.payload })),
    on(productActions.getPublishedContentBoxFailure, (state, action) => ({
        ...state,
        contentBox: undefined,
        error: action.payload,
    })),

    on(productActions.lookupInternationalTopUpOffersOrOperators, (state) => ({
        ...state,
        internationalTopUpLookupResult: undefined,
    })),
    on(productActions.onLookupInternationalTopUpOffersOrOperatorsSucceeded, (state, action) => ({
        ...state,
        internationalTopUpLookupResult: action.payload,
        error: undefined,
    })),
    on(productActions.onLookupInternationalTopUpOffersOrOperatorsFailed, (state, action) => ({
        ...state,
        internationalTopUpLookupResult: undefined,
        error: action.payload,
    })),
    on(productActions.setInternationalTopUpLookupLoadingState, (state, action) => ({
        ...state,
        isInternationalTopUpLookupLoading: action.payload,
    })),
    on(productActions.setInternationalTopUpPhoneContext, (state, action) => ({
        ...state,
        internationalTopUpPhoneContext: action.payload,
    })),
    on(productActions.setInternationalTopUpSelectedOffer, (state, action) => ({
        ...state,
        internationalTopUpSelectedOffer: action.payload,
    })),
    on(productActions.selectInternationalTopUpOffer, (state) => ({
        ...state,
        isInternationalTopUpOfferSelectionSucceeded: undefined,
        error: undefined,
    })),
    on(productActions.onSelectInternationalTopUpOfferSucceeded, (state) => ({
        ...state,
        isInternationalTopUpOfferSelectionSucceeded: true,
        error: undefined,
    })),
    on(productActions.onSelectInternationalTopUpOfferFailed, (state, action) => ({
        ...state,
        isInternationalTopUpOfferSelectionSucceeded: false,
        error: action.payload,
    })),
    on(productActions.setInternationalTopUpSelectedCarrier, (state, action) => ({
        ...state,
        internationalTopUpSelectedCarrier: action.payload,
    })),
);

export function reducer(state: FeatureState | undefined, action: Action) {
    return productsReducer(state, action);
}

// Reducer Declarations - END
